import * as React from "react";

import { Button, Form, Modal } from "semantic-ui-react";
import * as moment from "moment";

import { SaveDispatchProps } from "@neworbit/simpleui-forms";
import { Input } from "@neworbit/simpleui-input";

import { useDispatch, useSelector } from "@teamprevent/admin-redux";

import { IsLoadingButton } from "@teamprevent/common/IsLoadingButton";

import { goToParent } from "@teamprevent/common/routing";

import { ApplicationState } from "../../applicationState";

import { ImmunisationType, loadImmunisationTypes } from "../../crud/immunisationType";
import { ImmunisationCaseCreateModel } from "../../crud/ohCase/model";
import { createImmunisationCase } from "../../crud/ohCase/actions";
import { routeIdSelector as customerIdSelector } from "../../crud/customer/selectors";
import { routeIdSelector as employeeRecordIdSelector } from "../../crud/employeeRecord/selectors";
import { routeIdSelector as ohCaseIdSelector } from "../../crud/ohCase/selectors";
import { CREATE_IMMUNISATION_CASE } from "../../crud/ohCase/actiontypes";

export interface CreateModalProps {
    employeeRecordId: string;
    customerId: string;
    open: boolean;
    immunisationTypes: ImmunisationType[];
    basePath: string;
}

export interface CreateProps {
    immunisationTypes: ImmunisationType[];
}

export interface DispatchProps extends SaveDispatchProps<ImmunisationCaseCreateModel> {
    close: () => void;
}

export const CreateLinkedImmunisationCaseModal: React.FunctionComponent = () => {
    const dispatch = useDispatch();
    const immunisationTypes = useSelector((state: ApplicationState) => state.immunisationTypes);
    const open = useSelector((state: ApplicationState) => state.router.pathname.endsWith("/createImmunisationCase"));
    const customerId = useSelector(customerIdSelector);
    const employeeRecordId = useSelector(employeeRecordIdSelector);
    const linkedOhCaseId = useSelector(ohCaseIdSelector);

    const [immunisationTypeIds, setImmunisationTypeIds] = React.useState<string[]>([]);
    const [dueDate, setDueDate] = React.useState<moment.Moment>(moment());
    const [submitted, setSubmitted] = React.useState(false);

    const onClose = React.useCallback(() => dispatch(goToParent()), [dispatch]);
    const onCreate = React.useCallback(async () => {
        setSubmitted(true);

        if (immunisationTypeIds.length === 0) {
            return;
        }

        const model: ImmunisationCaseCreateModel = {
            dueDate,
            immunisationTypeIds,
            linkedOhCaseId
        };
        await dispatch(createImmunisationCase(customerId, employeeRecordId, model));
        dispatch(goToParent());
    }, [customerId, dispatch, dueDate, employeeRecordId, immunisationTypeIds, linkedOhCaseId]);

    React.useEffect(() => {
        dispatch(loadImmunisationTypes());
    }, [dispatch]);

    return (
        <Modal open={open} onClose={onClose} as={Form} size="small" closeOnDimmerClick={false}>
            <Modal.Header>Create Immunisation Case</Modal.Header>
            <Modal.Content>
                <Input.DropdownMulti
                    value={immunisationTypeIds}
                    label="Immunisations *"
                    showErrors={submitted}
                    required
                    onChange={setImmunisationTypeIds}
                    dynamicOptions
                    options={immunisationTypes.map(t => ({ value: t.id, text: t.name }))}
                />
                <Input.Date
                    value={dueDate}
                    label="Date *"
                    required
                    showErrors={submitted}
                    onChange={setDueDate}
                />

            </Modal.Content>
            <Modal.Actions>
                <Button content="Cancel" onClick={onClose} />
                <IsLoadingButton content="Create" onClick={onCreate} requestNames={CREATE_IMMUNISATION_CASE} />
            </Modal.Actions>
        </Modal>
    );
};
